/**
 * @file 文件传输组件(导入本地文件，转换为在线文档)
 */
import {FC, useState, useEffect} from 'react';
import cx from 'classnames';

import Icon, {IconSize} from '@/components/Icon';
import Popover from '@/components/Popover';
import Checkbox from '@/components/Checkbox';
import Button, {ButtonShape, ButtonType, ButtonSize} from '@/components/Button';
import {
    ImportSuccessSvg,
    ImportLoadingSvg,
    ImportErrorSvg,
    DialogCloseSvg,
    DialogCollapseSvg,
} from '@/icons';
import * as store from '@/store';
import sdk from '@/sdk';
import type {FileItem} from '@/types/document/file';
import {FileStatus, FileType} from '@/constants/document/file';
import {useUploadFile} from '@/hooks/document/useUploadFile';
import ImportSuccessGif from '@/assets/img/import-success.gif';
import ImportErrorGif from '@/assets/img/import-error.gif';
import ImportLoadingGif from '@/assets/img/import-loading.gif';
import {feLogger, importExcelFromClient} from '@/buried';

import FileList from './FileList';
import styles from './index.module.scss';

// 关闭正在导入的文件传输组件是否需要提示，默认有提示，有缓存则不提示
const CloseTipStorageKey = 'Close_FileTransfer_Tip';

const getComputedData = (fileList: FileItem[]) => {
    const totalLength = fileList.length;
    let successNum = 0;
    let errorNum = 0;
    let progressNum = 0;
    let initNum = 0;

    fileList.forEach(file => {
        switch (file.status) {
            case FileStatus.Progress:
                progressNum++;
                break;
            case FileStatus.Error:
                errorNum++;
                break;
            case FileStatus.Success:
                successNum++;
                break;
            case FileStatus.Init:
                initNum++;
                break;
            default:
                break;
        }
    });

    return {
        totalLength,
        successNum,
        errorNum,
        progressNum,
        initNum,
    };
};

const FileTransfer: FC = () => {
    const {uploadFileFromClient, handleRetryAll, handleCancelAll} = useUploadFile();
    const [isMini, setIsMini] = useState(false);
    const [tipChecked, setTipChecked] = useState(false);

    const clientImportFileData = store.getClientImportFileData();
    const importFileList = store.getImportFileList();
    const {totalLength, successNum, errorNum, progressNum, initNum} = getComputedData(importFileList);
    const isNeedCloseTip = !localStorage.getItem(CloseTipStorageKey) && progressNum > 0;

    useEffect(() => {
        if (clientImportFileData?.fid && clientImportFileData.chatId && clientImportFileData.chatType) {
            feLogger(
                importExcelFromClient({
                    pageid: '',
                    userid: sdk.user && sdk.user?.userGuid,
                    type: clientImportFileData.importType,
                })
            );

            uploadFileFromClient(clientImportFileData, clientImportFileData.importType as FileType);
            store.setClientImportFileData({});
        }
    }, [clientImportFileData, uploadFileFromClient]);

    const handleClickCollapse = () => {
        setIsMini(!isMini);
    };

    const clearList = () => {
        handleCancelAll();
        store.setImportFileList([]);
    };

    const handleClickClose = () => {
        if (isNeedCloseTip) {
            return;
        }
        clearList();
    };

    const handleConfirmClose = () => {
        if (tipChecked) {
            localStorage.setItem(CloseTipStorageKey, '1');
        }
        clearList();
    };

    const handleClickRetryAll = () => {
        const errorFileList = importFileList.filter(file => file.status === FileStatus.Error);
        handleRetryAll(errorFileList);
    };

    const handleTipChange = (_e: any, checked: boolean) => {
        setTipChecked(checked);
    };

    const renderStatusIcon = () => {
        if (errorNum > 0) {
            return ImportErrorSvg;
        } else if (totalLength === successNum) {
            return ImportSuccessSvg;
        }
        return ImportLoadingSvg;
    };

    const renderStatusImg = () => {
        let src = ImportLoadingGif;
        if (progressNum > 0) {
            src = ImportLoadingGif;
        } else if (initNum > 0) {
            return <Icon svg={ImportLoadingSvg} size={IconSize.XXL} noSpace />;
        } else if (errorNum > 0) {
            src = ImportErrorGif;
        } else if (totalLength === successNum) {
            src = ImportSuccessGif;
        }
        return <img src={src} alt="" />;
    };

    const renderStatusText = () => {
        let statusText = '正在导入';
        if (progressNum > 0) {
            statusText = '正在导入';
        } else if (initNum > 0) {
            statusText = '等待导入';
        } else if (errorNum > 0) {
            statusText = '导入失败';
        } else if (totalLength === successNum) {
            statusText = '导入完成';
        }
        return `${statusText}（${successNum}/${totalLength}）`;
    };

    const renderClosePopover = () => {
        return (
            <div className={styles.closePanel}>
                <div className={styles.tip}>关闭弹窗后，已开始的导入进程将在后台继续进行。</div>
                <Checkbox
                    className={styles.checkbox}
                    size={'sm'}
                    checked={tipChecked}
                    onChange={handleTipChange}
                >
                    下次不再提示
                </Checkbox>
                <Button
                    className={styles.button}
                    onClick={handleConfirmClose}
                    size={ButtonSize.SM}
                    type={ButtonType.Important}
                    shape={ButtonShape.Capsule}
                >
                    <span>知道了</span>
                </Button>
            </div>
        );
    };

    return totalLength > 0 ? (
        <div className={styles.main}>
            <div className={cx(styles.header, !isMini && styles.underline)}>
                <div className={styles.left}>
                    {/* <Icon svg={renderStatusIcon()} size={IconSize.XXL} noSpace /> */}
                    {renderStatusImg()}
                    <span className={styles.statusText}>{renderStatusText()}</span>
                </div>
                <div className={styles.right}>
                    {errorNum > 0 && <span className={styles.retryText} onClick={handleClickRetryAll}>重试</span>}
                    <Icon
                        noSpace
                        className={cx(styles.icon, isMini && styles.rotate)}
                        svg={DialogCollapseSvg}
                        size={IconSize.XL}
                        onClick={handleClickCollapse}
                    />
                    <Popover
                        width={238}
                        withArrow={false}
                        placement={'top-end'}
                        beforeChange={() => isNeedCloseTip}
                        content={renderClosePopover()}
                    >
                        <Icon
                            noSpace
                            className={styles.icon}
                            svg={DialogCloseSvg}
                            size={IconSize.XL}
                            onClick={handleClickClose}
                        />
                    </Popover>
                </div>
            </div>
            <div className={cx(styles.body, isMini && styles.hidden)}>
                <FileList fileList={importFileList.reverse()} />
            </div>
        </div>
    ) : null;
};

export default store.connect([
    store.KEYS.IMPORT_FILE_LIST,
    store.KEYS.CLIENT_IMPORT_FILE_DATA,
])(FileTransfer);

